import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  createManualBankTransferInvoice,
  previewManualBankTransferInvoice,
  registerExternalAccountingInvoice,
} from '../api/billingPortalApi';
import { quoteCustomerPriceAgreement } from '../api/priceAgreementsApi';
import {
  commercialAgreementsControls,
  commercialAgreementsEnabledFromOverview,
  isCommercialAgreementsUnavailableError,
} from '../billing/commercialAgreementsUi';
import { defaultDueDateIso } from '../billing/invoiceDueDisplay';
import { colorTokens } from '../app/designTokens';
import { SectionCard } from './SectionCard';
import { Button } from './ui/Button';
import { Select, SelectOption } from './ui/Select';
import { TextInput } from './ui/TextInput';

const { semantic } = colorTokens;

function resolveMessage(payload, t, fallback) {
  const rawKey = payload?.message_key;
  if (rawKey) {
    const i18nKey = String(rawKey).startsWith('billing.')
      ? `billing:${String(rawKey).slice('billing.'.length)}`
      : rawKey;
    return t(i18nKey, { defaultValue: payload.message || fallback });
  }
  return payload?.message || fallback;
}

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `mbt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Two-mode admin invoice issuance:
 * (A) Issue in MafertSystem  (B) Register external OMEGA invoice
 */
export function InvoiceIssuanceModesPanel({
  customerId,
  subscriptions = [],
  canManageInvoices,
  agreementsUnavailable,
  setAgreementsUnavailable,
  overview,
  busy,
  setBusy,
  setMessage,
  setError,
  onIssued,
}) {
  const { t } = useTranslation(['billing', 'common']);
  const [mode, setMode] = useState('system');
  const [systemForm, setSystemForm] = useState({
    subscription_id: '',
    tenant_id: '',
    due_at: defaultDueDateIso(14),
    billing_interval: 'monthly',
  });
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bankInvoice, setBankInvoice] = useState(null);
  const issueLockRef = useRef(false);
  const idempotencyRef = useRef(newIdempotencyKey());

  const [externalForm, setExternalForm] = useState({
    subscription_id: '',
    tenant_id: '',
    external_invoice_number: '',
    amount: '',
    currency: 'EUR',
    issued_at: new Date().toISOString().slice(0, 10),
    due_at: defaultDueDateIso(14),
    coverage_period_start: new Date().toISOString().slice(0, 10),
    coverage_period_end: defaultDueDateIso(365),
    description: '',
    note: '',
    variance_reason: '',
    billing_interval: 'monthly',
  });
  const [expectedQuote, setExpectedQuote] = useState(null);
  const [externalInvoice, setExternalInvoice] = useState(null);

  const agreementsUi = commercialAgreementsControls({
    hasPermission: true,
    overviewEnabled: commercialAgreementsEnabledFromOverview(overview),
    unavailable: agreementsUnavailable,
  });

  useEffect(() => {
    const first = subscriptions[0];
    if (!first) return;
    setSystemForm((prev) => ({
      ...prev,
      subscription_id: prev.subscription_id || String(first.id),
      tenant_id: prev.tenant_id || String(first.tenant_id || ''),
      billing_interval: first.billing_interval || prev.billing_interval,
    }));
    setExternalForm((prev) => ({
      ...prev,
      subscription_id: prev.subscription_id || String(first.id),
      tenant_id: prev.tenant_id || String(first.tenant_id || ''),
      billing_interval: first.billing_interval || prev.billing_interval,
    }));
  }, [subscriptions]);

  const selectedSubscription = useMemo(
    () => subscriptions.find((row) => String(row.id) === String(systemForm.subscription_id)),
    [subscriptions, systemForm.subscription_id],
  );

  const refreshPreview = async (formOverride = null) => {
    const form = formOverride || systemForm;
    if (!customerId || !form.subscription_id) {
      setPreview(null);
      return;
    }
    setPreviewError('');
    try {
      const data = await previewManualBankTransferInvoice({
        customer_id: Number(customerId),
        subscription_id: Number(form.subscription_id),
        tenant_id: form.tenant_id ? Number(form.tenant_id) : undefined,
        due_at: form.due_at || undefined,
      });
      setPreview(data);
      if (data?.due_at && !formOverride) {
        setSystemForm((prev) => (prev.due_at ? prev : { ...prev, due_at: data.due_at }));
      }
    } catch (err) {
      setPreview(null);
      setPreviewError(resolveMessage(err?.response?.data || {}, t, t('billing:manualBankTransfer.previewFailed')));
    }
  };

  useEffect(() => {
    if (mode !== 'system' || !canManageInvoices) return undefined;
    const handle = setTimeout(() => {
      refreshPreview();
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, customerId, systemForm.subscription_id, systemForm.due_at, canManageInvoices]);

  const refreshExpectedQuote = async (formOverride = null) => {
    const form = formOverride || externalForm;
    if (!customerId || !form.subscription_id || !form.coverage_period_start) {
      setExpectedQuote(null);
      return;
    }
    if (!agreementsUi.allowQuoteHint) {
      setExpectedQuote(null);
      return;
    }
    try {
      const data = await quoteCustomerPriceAgreement(customerId, {
        subscription_id: Number(form.subscription_id),
        billing_interval: form.billing_interval || 'monthly',
        coverage_period_start: form.coverage_period_start,
      });
      setExpectedQuote(data);
    } catch (err) {
      if (isCommercialAgreementsUnavailableError(err) && setAgreementsUnavailable) {
        setAgreementsUnavailable(true);
      }
      setExpectedQuote(null);
    }
  };

  const onSystemSubscriptionChange = (subscriptionId) => {
    const selected = subscriptions.find((row) => String(row.id) === String(subscriptionId));
    const next = {
      ...systemForm,
      subscription_id: String(subscriptionId),
      tenant_id: selected?.tenant_id ? String(selected.tenant_id) : systemForm.tenant_id,
      billing_interval: selected?.billing_interval || systemForm.billing_interval || 'monthly',
    };
    setSystemForm(next);
  };

  const onExternalSubscriptionChange = (subscriptionId) => {
    const selected = subscriptions.find((row) => String(row.id) === String(subscriptionId));
    const next = {
      ...externalForm,
      subscription_id: String(subscriptionId),
      tenant_id: selected?.tenant_id ? String(selected.tenant_id) : externalForm.tenant_id,
      billing_interval: selected?.billing_interval || externalForm.billing_interval || 'monthly',
    };
    setExternalForm(next);
    refreshExpectedQuote(next);
  };

  const issueSystemInvoice = async () => {
    if (issueLockRef.current || busy) return;
    issueLockRef.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    setBankInvoice(null);
    try {
      const data = await createManualBankTransferInvoice({
        customer_id: Number(customerId),
        subscription_id: systemForm.subscription_id ? Number(systemForm.subscription_id) : undefined,
        tenant_id: systemForm.tenant_id ? Number(systemForm.tenant_id) : undefined,
        due_at: systemForm.due_at || undefined,
        generate_pdf: true,
        idempotency_key: idempotencyRef.current,
        confirm: true,
      });
      setBankInvoice(data.invoice || data);
      const qrStatus = data.invoice?.payment_qr_status || data.qr?.status;
      if (qrStatus === 'generated' || data.idempotent_reuse) {
        setMessage(
          data.idempotent_reuse
            ? t('billing:manualBankTransfer.idempotentReuse')
            : t('billing:manualBankTransfer.created'),
        );
      } else {
        setError(
          data.qr?.safe_error_message
            || t('billing:qa.qrNotGenerated', { status: qrStatus || 'unavailable' }),
        );
      }
      idempotencyRef.current = newIdempotencyKey();
      setConfirmOpen(false);
      if (onIssued) await onIssued();
    } catch (err) {
      setError(resolveMessage(err?.response?.data || {}, t, t('billing:manualBankTransfer.createFailed')));
    } finally {
      issueLockRef.current = false;
      setBusy(false);
    }
  };

  const registerExternalInvoice = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    setExternalInvoice(null);
    try {
      const data = await registerExternalAccountingInvoice({
        customer_id: Number(customerId),
        tenant_id: Number(externalForm.tenant_id),
        subscription_id: Number(externalForm.subscription_id),
        external_invoice_number: externalForm.external_invoice_number.trim(),
        amount: Number(externalForm.amount),
        currency: externalForm.currency || 'EUR',
        issued_at: externalForm.issued_at,
        due_at: externalForm.due_at,
        coverage_period_start: externalForm.coverage_period_start,
        coverage_period_end: externalForm.coverage_period_end,
        description: externalForm.description || undefined,
        note: externalForm.note || undefined,
        billing_interval: externalForm.billing_interval || 'monthly',
        variance_reason: externalForm.variance_reason || undefined,
      });
      setExternalInvoice(data.invoice || data);
      setMessage(t('billing:externalAccounting.registered'));
      if (onIssued) await onIssued();
    } catch (err) {
      setError(resolveMessage(err?.response?.data || {}, t, t('billing:externalAccounting.registerFailed')));
    } finally {
      setBusy(false);
    }
  };

  if (!canManageInvoices) {
    return null;
  }

  return (
    <SectionCard variant="Default" title={t('billing:issuance.modesTitle')} data-testid="invoice-issuance-modes">
      <Typography sx={{ mb: 1.5, fontSize: 13, color: semantic.text.secondary }}>
        {t('billing:issuance.modesHint')}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button
          variant={mode === 'system' ? 'Primary' : 'Secondary'}
          onClick={() => setMode('system')}
          data-testid="issue-mode-system"
        >
          {t('billing:issuance.modeSystem')}
        </Button>
        <Button
          variant={mode === 'external' ? 'Primary' : 'Secondary'}
          onClick={() => setMode('external')}
          data-testid="issue-mode-external"
        >
          {t('billing:issuance.modeExternal')}
        </Button>
      </Stack>

      {mode === 'system' && (
        <Stack spacing={1.5} sx={{ maxWidth: 560 }}>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('billing:manualBankTransfer.createHint')}
          </Typography>
          <Select
            label={t('billing:subscription')}
            value={systemForm.subscription_id}
            onChange={(e) => onSystemSubscriptionChange(e.target.value)}
            hideLabel={false}
          >
            {subscriptions.map((row) => (
              <SelectOption key={row.id} value={String(row.id)}>
                #{row.id} · tenant {row.tenant_id} · {row.status} · {row.billing_interval || 'monthly'}
              </SelectOption>
            ))}
          </Select>
          {selectedSubscription && (
            <Alert severity="info" data-testid="system-issue-subscription-meta">
              {t('billing:issuance.interval')}: {selectedSubscription.billing_interval || systemForm.billing_interval}
              {' · '}
              {t('billing:issuance.userTier')}: {selectedSubscription.users_count || preview?.users_count || '—'}
            </Alert>
          )}
          <TextInput
            label={t('billing:issuance.dueAt')}
            type="date"
            value={systemForm.due_at}
            onChange={(e) => setSystemForm((prev) => ({ ...prev, due_at: e.target.value }))}
            data-testid="system-issue-due-at"
          />
          <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
            {t('billing:issuance.dueDefaultHint', { days: preview?.due_days_default || 14 })}
          </Typography>
          {previewError && <Alert severity="warning">{previewError}</Alert>}
          {preview && (
            <Box data-testid="system-issue-preview" sx={{ p: 1.5, border: `1px solid ${semantic.border.subtle}`, borderRadius: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                {t('billing:issuance.commercialPreview')}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t('billing:issuance.catalogAmount')}: {preview.catalog_amount} {preview.currency}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t('billing:issuance.commercialDiscount')}: {preview.commercial_discount_amount} {preview.currency}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t('billing:issuance.commercialUplift')}: {preview.commercial_uplift_amount} {preview.currency}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t('billing:issuance.pay11Benefit')}: {preview.pay11_benefit_amount} {preview.currency}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t('billing:issuance.finalCharge')}: {preview.final_amount ?? preview.subtotal} {preview.currency}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t('billing:issuance.taxPreview')}: {preview.tax_amount} {preview.currency}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {t('billing:total')}: {preview.total} {preview.currency}
              </Typography>
              <Typography sx={{ fontSize: 12, color: semantic.text.muted, mt: 1 }}>
                {t('billing:issuance.numberDeferred')}
              </Typography>
              {(preview.lines || []).length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {(preview.lines || []).map((line, idx) => (
                    <Typography key={`${line.description}-${idx}`} sx={{ fontSize: 12, color: semantic.text.secondary }}>
                      • {line.description}: {line.amount} {preview.currency}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
          <Button
            variant="Primary"
            disabled={busy || !systemForm.subscription_id || !preview}
            onClick={() => setConfirmOpen(true)}
            data-testid="system-issue-open-confirm"
          >
            {t('billing:issuance.confirmIssue')}
          </Button>
          {bankInvoice && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('billing:invoice_number')}: {bankInvoice.public_number}
              </Typography>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('billing:invoices.qr.status')}: {bankInvoice.payment_qr_status}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <Button
                  variant="Secondary"
                  component={RouterLink}
                  to={`/billing/invoices/${bankInvoice.id}`}
                >
                  {t('billing:manualBankTransfer.openInvoice')}
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {mode === 'external' && (
        <Stack spacing={1.5} sx={{ maxWidth: 520 }}>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('billing:externalAccounting.registerHint')}
          </Typography>
          <Select
            label={t('billing:subscription')}
            value={externalForm.subscription_id}
            onChange={(e) => onExternalSubscriptionChange(e.target.value)}
            hideLabel={false}
          >
            {subscriptions.map((row) => (
              <SelectOption key={row.id} value={String(row.id)}>
                #{row.id} · tenant {row.tenant_id} · {row.status}
              </SelectOption>
            ))}
          </Select>
          <TextInput
            label={t('billing:externalAccounting.externalNumber')}
            value={externalForm.external_invoice_number}
            onChange={(e) => setExternalForm((prev) => ({ ...prev, external_invoice_number: e.target.value }))}
          />
          {expectedQuote && (
            <Alert severity="info" data-testid="omega-expected-amount">
              {t('billing:externalAccounting.expectedAmount')}: {expectedQuote.final_amount} {expectedQuote.currency}
              {' · '}
              {t('billing:externalAccounting.amountMismatchHint')}
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <TextInput
              label={t('billing:amount')}
              value={externalForm.amount}
              onChange={(e) => setExternalForm((prev) => ({ ...prev, amount: e.target.value }))}
              onBlur={() => refreshExpectedQuote()}
            />
            <TextInput
              label={t('billing:currency')}
              value={externalForm.currency}
              onChange={(e) => setExternalForm((prev) => ({ ...prev, currency: e.target.value }))}
            />
          </Stack>
          <TextInput
            label={t('billing:externalAccounting.varianceReason')}
            value={externalForm.variance_reason}
            onChange={(e) => setExternalForm((prev) => ({ ...prev, variance_reason: e.target.value }))}
            data-testid="omega-variance-reason"
          />
          <Stack direction="row" spacing={1}>
            <TextInput
              label={t('billing:externalAccounting.issuedAt')}
              type="date"
              value={externalForm.issued_at}
              onChange={(e) => setExternalForm((prev) => ({ ...prev, issued_at: e.target.value }))}
            />
            <TextInput
              label={t('billing:externalAccounting.dueAt')}
              type="date"
              value={externalForm.due_at}
              onChange={(e) => setExternalForm((prev) => ({ ...prev, due_at: e.target.value }))}
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextInput
              label={t('billing:externalAccounting.coverageStart')}
              type="date"
              value={externalForm.coverage_period_start}
              onChange={(e) => setExternalForm((prev) => ({ ...prev, coverage_period_start: e.target.value }))}
            />
            <TextInput
              label={t('billing:externalAccounting.coverageEnd')}
              type="date"
              value={externalForm.coverage_period_end}
              onChange={(e) => setExternalForm((prev) => ({ ...prev, coverage_period_end: e.target.value }))}
            />
          </Stack>
          <TextInput
            label={t('billing:note')}
            value={externalForm.note}
            onChange={(e) => setExternalForm((prev) => ({ ...prev, note: e.target.value }))}
          />
          <Button
            variant="Primary"
            disabled={busy || !externalForm.subscription_id || !externalForm.tenant_id}
            onClick={registerExternalInvoice}
            data-testid="omega-register"
          >
            {t('billing:externalAccounting.register')}
          </Button>
          {externalInvoice && (
            <Box>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('billing:externalAccounting.externalNumber')}: {externalInvoice.external_invoice_number || externalInvoice.display_number}
              </Typography>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('billing:invoices.pdf.status')}: {t('billing:externalAccounting.pdfNotApplicable')}
              </Typography>
              <Button
                variant="Secondary"
                component={RouterLink}
                to={`/billing/invoices/${externalInvoice.id}`}
                sx={{ mt: 1.5 }}
              >
                {t('billing:externalAccounting.openInvoice')}
              </Button>
            </Box>
          )}
        </Stack>
      )}

      <Dialog open={confirmOpen} onClose={() => (!busy ? setConfirmOpen(false) : null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing:issuance.confirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {t('billing:issuance.confirmBody', {
              total: preview?.total,
              currency: preview?.currency || 'EUR',
              due: systemForm.due_at,
            })}
          </Typography>
          <Alert severity="warning">{t('billing:issuance.confirmNoEmail')}</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="Ghost" disabled={busy} onClick={() => setConfirmOpen(false)}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="Primary"
            disabled={busy}
            loading={busy}
            onClick={issueSystemInvoice}
            data-testid="system-issue-confirm"
          >
            {t('billing:issuance.confirmIssue')}
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
}
